/**
 * The constants for the pricing v3
 * Here we define the base prices for the different plans and the credit and domain tiers.
 * The actual product and prices are on Stripe.
 * You will need to run the scripts/stripe-utilities/create-stripe-prices-runner.ts to create the prices on Stripe.
 * `pnpm tsx scripts/stripe-utilities/create-stripe-prices-runner.ts`
 * Then, run
 * `pnpm tsx scripts/stripe/sync-stripe-prices-to-db.ts` to sync the prices to our database.
 *
 */
export const basicBasePrice = 14.99
export const premiumBasePrice = 24.99
export const platinumBasePrice = 27.99

export const premiumBaseCredit = 500
export const platinumBaseCredit = 1600

export const annualDiscount = 0.8
export const creditTiers = [
  { value: 200, price: 15 },
  { value: 500, price: 25 }, // + 15
  { value: 800, price: 40 }, // + 15
  { value: 1200, price: 56 }, // + 16
  { value: 1600, price: 72 }, // + 16
  { value: 2000, price: 88 }, // + 16
  { value: 3000, price: 128 }, // + 40
  { value: 4000, price: 168 }, // + 40
  { value: 6000, price: 248 }, // + 80
  { value: 8000, price: 328 }, // + 80
  { value: 10000, price: 408 }, // + 80
]

/**
 * Price per additional domain beyond the first one
 */
export const DOMAIN_ADDON_PRICE = 19.99

/**
 * Domain pricing tiers - incremental pricing model
 * Each additional domain costs $19.99/month
 */
export const domainTiers = [
  { value: 1, price: 0 }, // First domain included with Basic+ plan
  { value: 2, price: 19.99 }, // +1 domain = $19.99
  { value: 3, price: 39.98 }, // +2 domains = $39.98
  { value: 4, price: 59.97 }, // +3 domains = $59.97
  { value: 5, price: 79.96 }, // +4 domains = $79.96
  { value: 10, price: 179.91 }, // +9 domains = $179.91
  { value: 999, price: 120 }, // Unlimited domains bundle
]

/**
 * Calculate the price for a given plan configuration
 * @param plan - Plan type (basic, premium, platinum)
 * @param credits - Credit amount
 * @param domains - Domain count
 * @returns Calculated price with annual discount applied if applicable
 */
export const calculateCurrentPrice = (
  plan: 'basic' | 'premium' | 'platinum',
  credits: number,
  domains: number,
  annual: boolean,
) => {
  let basePrice = 0
  switch (plan) {
    case 'basic':
      basePrice = basicBasePrice
      break
    case 'premium':
      basePrice = premiumBasePrice
      break
    case 'platinum':
      basePrice = platinumBasePrice
      break
  }
  const creditTier = creditTiers.find((opt) => opt.value === credits)
  if (!creditTier) {
    throw new Error(`Invalid credits: ${credits}`)
  }
  const domainTier = domainTiers.find((opt) => opt.value === domains)
  if (!domainTier) {
    throw new Error(`Invalid domains: ${domains}`)
  }
  const totalPrice =
    basePrice + (creditTier?.price || 0) + (domainTier?.price || 0)
  return annual ? totalPrice * annualDiscount : totalPrice
}
